import { INSTRUMENTS_BY_TYPE, InstrumentType } from '@fretgenius/core/music-theory-service';
import { reportUiError } from '../../error/reporting';

type ResolveStage = 'init' | 'setTuningId' | 'hydrate';
type ValidateStage = 'init' | 'setInstrumentType';

export function resolveValidTuningId(
  instrumentType: InstrumentType,
  requestedTuningId: string | undefined,
  stage: ResolveStage,
  reportErrorFn: (error: unknown, context?: Record<string, unknown>) => void
): string {
  const instrDef = INSTRUMENTS_BY_TYPE[instrumentType];
  const defaultTuningId = instrDef?.defaultTunings[0]?.id ?? '';

  if (!instrDef) {
    reportUiError(
      reportErrorFn,
      'fretboard.config.missingInstrumentDefaults',
      new Error('Instrument definition not found for selected type'),
      { stage, instrumentType }
    );
    return defaultTuningId;
  }

  if (!requestedTuningId) return defaultTuningId;

  const isValid = instrDef.defaultTunings.some((t) => t.id === requestedTuningId);
  if (!isValid) {
    reportUiError(
      reportErrorFn,
      'fretboard.config.invalidTuning',
      new Error('Requested tuning is not valid for instrument'),
      { stage, instrumentType, requestedTuningId, fallbackTuningId: defaultTuningId }
    );
    return defaultTuningId;
  }
  return requestedTuningId;
}

export function validateInstrumentDefaults(
  instrumentType: InstrumentType,
  stage: ValidateStage,
  reportErrorFn: (error: unknown, context?: Record<string, unknown>) => void
): void {
  const instrDef = INSTRUMENTS_BY_TYPE[instrumentType];
  if (!instrDef) {
    reportUiError(
      reportErrorFn,
      'fretboard.config.missingInstrumentDefaults',
      new Error('Instrument definition not found for selected type'),
      { stage, instrumentType }
    );
    return;
  }
  if (!instrDef.defaultTunings || instrDef.defaultTunings.length === 0) {
    reportUiError(
      reportErrorFn,
      'fretboard.config.missingInstrumentDefaults',
      new Error('No default tunings defined for instrument'),
      { stage, instrumentType }
    );
  }
  if (typeof instrDef.defaultNumFrets !== 'number') {
    reportUiError(
      reportErrorFn,
      'fretboard.config.missingInstrumentDefaults',
      new Error('No default number of frets defined for instrument'),
      { stage, instrumentType }
    );
  }
}
